import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEur } from "../api";
import { useLang } from "../i18n";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Shield, Lock, EyeOff } from "lucide-react";

const SKY = "#38BDF8";

export const GuardianStatsGrid = ({ stats }) => {
  const { lang } = useLang();
  if (!stats) return null;
  const it = lang === "it";
  const avg = stats.years_total ? stats.total_eur / stats.years_total : 0;
  const cells = [
    { key: "years", label: it ? "Anni positivi" : "Positive years", value: `${stats.years_positive}/${stats.years_total}`, sub: it ? "dal 2009" : "since 2009" },
    { key: "total", label: it ? "Performance storica" : "Historical performance", value: fmtEur(stats.total_eur, lang), sub: `+${Math.round(stats.total_eur / stats.reference_capital * 100)}%` },
    { key: "avg", label: it ? "Media annua" : "Yearly average", value: fmtEur(avg, lang), sub: `+${(avg / stats.reference_capital * 100).toFixed(1)}%` },
    { key: "winrate", label: "Win rate", value: `${stats.win_rate}%`, sub: `${stats.n_trades} ops` },
    { key: "pf", label: "Profit factor", value: stats.profit_factor ?? "—", sub: null },
    { key: "dd", label: "Max drawdown", value: fmtEur(-stats.max_drawdown_eur, lang), sub: it ? "storico" : "historical", neg: true },
  ];
  return (
    <div>
      <div data-testid="guardian-stats-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-sky-500/20 divide-x divide-y divide-sky-500/10 md:divide-y-0">
        {cells.map((c) => (
          <div key={c.key} data-testid={`guardian-stat-${c.key}`} className="p-4 bg-[#0B1220]">
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{c.label}</p>
            <p className={`font-mono text-lg mt-1 ${c.neg ? "text-red-400" : "text-sky-400"}`}>{c.value}</p>
            {c.sub && <p className="font-mono text-xs text-slate-500">{c.sub}</p>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
        {lang === "it"
          ? "Le percentuali sono calcolate su un capitale di riferimento di 10.000 € (margine + riserva per il drawdown massimo storico). Backtest 2009-2026 su dati reali con fill conservativi."
          : "Percentages are calculated on a €10,000 reference capital (margin + reserve for the historical max drawdown). 2009-2026 backtest on real data with conservative fills."}
      </p>
    </div>
  );
};

export const GuardianYearly = () => {
  const { lang } = useLang();
  const [years, setYears] = useState([]);
  useEffect(() => { api.get("/guardian/performance/yearly").then((r) => setYears(r.data)).catch(() => {}); }, []);
  if (!years.length) return null;
  return (
    <div data-testid="guardian-yearly">
      <h2 className="text-base md:text-lg font-medium text-white mb-1">
        {lang === "it" ? "Performance annuali — mai un anno in perdita" : "Yearly performance — never a losing year"}
      </h2>
      <p className="text-xs text-slate-500 mb-4">
        {lang === "it" ? "Serie storica completa 2009-2026, pubblica e verificabile." : "Full 2009-2026 track record, public and verifiable."}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {years.map((y) => (
          <Link key={y.year} to={`/guardian/year/${y.year}`} data-testid={`guardian-year-${y.year}`}
            className="border border-sky-500/15 bg-[#0B1220] p-4 block hover:border-sky-400/50 hover:bg-sky-500/[0.06] transition-colors group">
            <span className="font-heading font-bold text-white group-hover:text-sky-300 transition-colors">{y.year}</span>
            <p className={`font-mono text-lg mt-2 ${y.pl_eur >= 0 ? "text-sky-400" : "text-red-400"}`}>
              {y.pl_eur >= 0 ? "+" : ""}{Math.round(y.pl_eur).toLocaleString(lang === "it" ? "it-IT" : "en-GB")} €
            </p>
            <p className={`font-mono text-xs ${y.pl_pct >= 0 ? "text-sky-400/70" : "text-red-400/70"}`}>
              {y.pl_pct >= 0 ? "+" : ""}{y.pl_pct}%
            </p>
            <p className="text-[10px] text-slate-500 font-mono mt-1">{y.n_trades} ops · WR {y.win_rate}%</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const GuardianEquityChart = ({ year, title: titleProp }) => {
  const { lang } = useLang();
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get("/guardian/equity", { params: year ? { year } : {} }).then((r) => setData(r.data)).catch(() => {});
  }, [year]);
  if (!data.length) return null;
  const title = titleProp || (year
    ? (lang === "it" ? `Equity line Guardian ${year} — anno in corso (1 contratto Bund)` : `Guardian equity line ${year} — current year (1 Bund contract)`)
    : (lang === "it" ? "Equity line Guardian — 1 contratto Bund, 2009-2026" : "Guardian equity line — 1 Bund contract, 2009-2026"));
  return (
    <div data-testid="guardian-equity-chart" className="border border-sky-500/20 bg-[#0B1220] p-4">
      <h3 className="text-sm font-medium text-slate-200 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="geq" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SKY} stopOpacity={0.25} />
              <stop offset="100%" stopColor={SKY} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} minTickGap={60} />
          <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} width={60}
            tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", fontSize: 12, fontFamily: "IBM Plex Mono" }}
            labelStyle={{ color: "#94A3B8" }} formatter={(v) => [`${Math.round(v).toLocaleString()} €`, "Equity"]} />
          <Area type="monotone" dataKey="equity" stroke={SKY} strokeWidth={1.5} fill="url(#geq)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const GuardianSignalCard = ({ signal }) => {
  const { lang } = useLang();
  const it = lang === "it";
  if (!signal) return null;
  const dirColor = signal.direction === "LONG" ? "text-sky-400" : signal.direction === "SHORT" ? "text-red-400" : "text-slate-300";
  const statusLabel = {
    locked: it ? "PROFIT LOCK — obiettivo annuale raggiunto" : "PROFIT LOCK — yearly target reached",
    suspended: it ? "SOSPESO — circuit breaker" : "SUSPENDED — circuit breaker",
    holding: it ? "POSIZIONE APERTA" : "OPEN POSITION",
    waiting: it ? "IN ATTESA — fuori mercato" : "WAITING — flat",
    vol_guard: it ? "FILTRO VOLATILITÀ" : "VOLATILITY GUARD",
    entry: it ? "SEGNALE D'INGRESSO" : "ENTRY SIGNAL",
    entry_extreme: it ? "INGRESSO — ECCESSO ESTREMO" : "ENTRY — EXTREME",
  }[signal.status] || "";
  return (
    <div data-testid="guardian-signal-card" className="border border-sky-500/30 bg-sky-500/5 p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-[0.2em] uppercase text-sky-300 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" /> {it ? "Segnale Guardian" : "Guardian signal"}
        </span>
        <span className="text-xs font-mono text-slate-500">{signal.date}</span>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <span data-testid="guardian-signal-direction" className={`text-3xl font-heading font-extrabold tracking-tight ${dirColor}`}>{signal.direction}</span>
        <span className="ml-auto font-mono text-xs text-slate-400">z {signal.z_score > 0 ? "+" : ""}{signal.z_score} · {signal.regime}</span>
      </div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-sky-400/80 mt-1">{statusLabel}</p>
      <p data-testid="guardian-signal-motivation" className="text-sm text-slate-300 mt-3 leading-relaxed">
        {it ? signal.motivation_it : signal.motivation_en}
      </p>
      {signal.sl_points && (
        <div className="mt-4 border border-sky-500/30 px-3 py-2 text-xs font-mono text-sky-200">
          {it
            ? `Ingresso in apertura · Stop loss ${signal.sl_points} pt · Take profit ${signal.tp_points} pt`
            : `Entry at the open · Stop loss ${signal.sl_points} pt · Take profit ${signal.tp_points} pt`}
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-4 font-mono">YTD: {fmtEur(signal.ytd_eur, lang)} · Profit lock: {fmtEur(2000, lang)}</p>
    </div>
  );
};

const Row = ({ tr, lang }) => (
  <tr className="border-t border-white/5">
    <td className="px-4 py-2 text-slate-300">{tr.contract}{tr.closed_by === "tp" ? " · TP" : tr.closed_by === "sl" ? " · SL" : ""}</td>
    <td className={`px-2 py-2 font-bold ${tr.direction === "LONG" ? "text-sky-400" : tr.direction === "SHORT" ? "text-red-400" : "text-slate-400"}`}>{tr.direction}</td>
    <td className="px-2 py-2 text-right text-slate-400">{tr.open_date}</td>
    <td className="px-2 py-2 text-right text-slate-300">{tr.open_price != null ? tr.open_price.toFixed(2) : "***"}</td>
    <td className="px-2 py-2 text-right text-slate-400">{tr.close_date || "—"}</td>
    <td className="px-2 py-2 text-right text-slate-300">{tr.close_price != null ? tr.close_price.toFixed(2) : "—"}</td>
    <td className={`px-2 py-2 text-right ${(tr.status === "open" ? tr.open_pl_eur : tr.pl_eur) >= 0 ? "text-sky-400" : "text-red-400"}`}>
      {(tr.status === "open" ? tr.open_pl_eur : tr.pl_eur) != null
        ? `${(tr.status === "open" ? tr.open_pl_eur : tr.pl_eur) > 0 ? "+" : ""}${Math.round(tr.status === "open" ? tr.open_pl_eur : tr.pl_eur).toLocaleString()}` : "—"}
    </td>
    <td className="px-4 py-2 text-right">
      <span className={`text-[10px] px-1.5 py-0.5 border ${tr.status === "open" ? "border-amber-500/40 text-amber-400" : "border-white/10 text-slate-500"}`}>
        {tr.status === "open" ? (lang === "it" ? "APERTA" : "OPEN") : (lang === "it" ? "CHIUSA" : "CLOSED")}
      </span>
    </td>
  </tr>
);

export const SystemsComparison = () => {
  const { lang } = useLang();
  const it = lang === "it";
  const rows = [
    [it ? "Profilo" : "Profile", it ? "Speculativo" : "Speculative", it ? "Conservativo" : "Conservative"],
    [it ? "Obiettivo" : "Goal",
      it ? "Massimizzare la performance" : "Maximise performance",
      it ? "Proteggere il capitale: 18 anni su 18 chiusi in utile" : "Protect capital: 18 out of 18 years closed in profit"],
    [it ? "Metodo" : "Method",
      it ? "Stop & Reverse: sempre a mercato, long o short" : "Stop & Reverse: always in the market, long or short",
      it ? "Fuori mercato di default: entra solo alle condizioni più favorevoli" : "Flat by default: enters only on the most favourable conditions"],
    [it ? "Operazioni / anno" : "Trades / year", "~10-12", it ? "~2-3 (anche solo 2)" : "~2-3 (even just 2)"],
    ["Stop loss", it ? "No (inversione di posizione)" : "No (position reversal)", it ? "Rigido: 2,5 punti su ogni trade" : "Hard: 2.5 points on every trade"],
    ["Take profit", it ? "3 punti (mean reversion)" : "3 points (mean reversion)", it ? "1,5 punti su ogni trade" : "1.5 points on every trade"],
    [it ? "Protezioni annuali" : "Yearly protections", "—",
      it ? "Profit lock +2.000 € · Circuit breaker −6.000 €" : "Profit lock +€2,000 · Circuit breaker −€6,000"],
    [it ? "Capitale di riferimento" : "Reference capital", "€5.000", "€10.000"],
    [it ? "Rendimento" : "Return",
      it ? "Più alto, più variabile" : "Higher, more variable",
      it ? "+29,6% medio annuo, molto stabile" : "+29.6% yearly average, very stable"],
    [it ? "A chi è rivolto" : "Who it's for",
      it ? "Trader che accetta oscillazioni in cambio del massimo rendimento" : "Traders accepting swings in exchange for maximum return",
      it ? "Investitore prudente che vuole costanza e protezione del capitale" : "Prudent investors who want consistency and capital protection"],
    [it ? "Abbonamento" : "Subscription",
      it ? "3 / 6 / 12 mesi · trial gratuito disponibile" : "3 / 6 / 12 months · free trial available",
      it ? "Solo annuale €1.350 · trial non ammesso" : "Annual only €1,350 · no trial"],
  ];
  return (
    <div data-testid="systems-comparison" className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#111111]">
            <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-slate-500 w-1/4"></th>
            <th className="text-left px-4 py-3">
              <span className="font-heading font-bold text-emerald-400">Bund Alpha</span>
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-normal mt-0.5">{it ? "Sistema speculativo" : "Speculative system"}</span>
            </th>
            <th className="text-left px-4 py-3">
              <span className="font-heading font-bold text-sky-400">Bund Guardian</span>
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-normal mt-0.5">{it ? "Sistema conservativo" : "Conservative system"}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/5">
              <td className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500">{r[0]}</td>
              <td className="px-4 py-2.5 text-slate-300 text-[13px]">{r[1]}</td>
              <td className="px-4 py-2.5 text-slate-300 text-[13px] bg-sky-500/[0.03]">{r[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const GuardianOps = () => {
  const { lang, t } = useLang();
  const it = lang === "it";
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/guardian/trades").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const full = data.access === "guardian";
  const head = (
    <thead className="sticky top-0 bg-[#0F172A]">
      <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
        <th className="text-left px-4 py-2">{t("col_contract")}</th>
        <th className="text-left px-2 py-2">{t("col_dir")}</th>
        <th className="text-right px-2 py-2">{t("col_open")}</th>
        <th className="text-right px-2 py-2">{t("col_open_price")}</th>
        <th className="text-right px-2 py-2">{t("col_close")}</th>
        <th className="text-right px-2 py-2">{t("col_close_price")}</th>
        <th className="text-right px-2 py-2">{t("col_pl")}</th>
        <th className="text-right px-4 py-2">{t("col_status")}</th>
      </tr>
    </thead>
  );
  return (
    <div data-testid="guardian-ops" className="border border-sky-500/20 bg-[#0B1220]">
      <div className="px-4 py-3 border-b border-sky-500/15 flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-200 flex items-center gap-2">
          <Shield className="w-4 h-4 text-sky-400" />
          {full ? (it ? "Operatività Guardian — accesso completo" : "Guardian operations — full access")
            : (it ? "Operatività Guardian" : "Guardian operations")}
        </h3>
        {full && <span className="text-xs font-mono text-slate-500">{data.trades.length} ops</span>}
      </div>
      {full ? (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs font-mono">
            {head}
            <tbody>{data.trades.map((tr) => <Row key={tr.id} tr={tr} lang={lang} />)}</tbody>
          </table>
        </div>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              {head}
              <tbody>
                {data.current && (
                  <tr data-testid="guardian-masked-row" className="border-t border-white/5 bg-sky-500/[0.04]">
                    <td className="px-4 py-2 text-slate-300">{data.current.contract}</td>
                    <td className="px-2 py-2 font-bold text-slate-500">***</td>
                    <td className="px-2 py-2 text-right text-slate-500">***</td>
                    <td className="px-2 py-2 text-right text-slate-500">***</td>
                    <td className="px-2 py-2 text-right text-slate-500">***</td>
                    <td className="px-2 py-2 text-right text-slate-500">***</td>
                    <td className={`px-2 py-2 text-right ${(data.current.pl_eur ?? 0) >= 0 ? "text-sky-400" : "text-red-400"}`}>
                      {data.current.pl_eur != null ? `${data.current.pl_eur > 0 ? "+" : ""}${Math.round(data.current.pl_eur).toLocaleString()}` : "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-[10px] px-1.5 py-0.5 border border-sky-500/40 text-sky-400 inline-flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> {it ? "RISERVATA" : "RESERVED"}
                      </span>
                    </td>
                  </tr>
                )}
                {data.last_closed && <Row tr={data.last_closed} lang={lang} />}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-sky-500/15 flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-slate-400 leading-relaxed">
              {data.current
                ? (it
                  ? `L'operazione in corso è riservata agli abbonati Guardian: direzione, data e prezzo d'ingresso restano oscurati; è mostrato solo il P/L in corso aggiornato al giorno precedente${data.current?.as_of ? ` (${data.current.as_of})` : ""}. I dati tornano in chiaro un giorno dopo la chiusura dell'operazione.`
                  : `The ongoing operation is reserved for Guardian subscribers: direction, date and entry price stay hidden; only the running P/L as of the previous session is shown${data.current?.as_of ? ` (${data.current.as_of})` : ""}. Data clears one day after the operation is closed.`)
                : (it
                  ? "Al momento il sistema è fuori mercato. Quando un'operazione è in corso, per i non abbonati resta riservata (direzione, data e prezzo oscurati) e torna in chiaro un giorno dopo la chiusura."
                  : "The system is currently flat. When an operation is ongoing, it stays reserved for non-subscribers (direction, date and price hidden) and clears one day after it is closed.")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
